// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// MUI Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { ViewFileIcon } from "../../../../assets/icons";

// Utilities
import {
  getExamCardText,
  getTooltipText,
} from "../../../../utils/examReportsHelper";
import { getLocalDate } from "../../../../utils/dateHelper";
import { getTruncatedText } from "../../../../utils/textHelper";
import useClinic from "../../../../hooks/useClinic";

const RecentlyCompletedExamCard = ({ row, isSelected, headCells }) => {
  // internationalization
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicTimezone } = useClinic();

  const recentlyCompletedHref = `/patients/${row?.patient?.id}/exam/${row?.id}`;
  const examTranslation = useMemo(() => getExamCardText(row, t), [row, t]);
  const examTooltipTranslation = useMemo(
    () => getTooltipText(row, t),
    [row, t]
  );

  const doneLocalTime = row?.completionDate
    ? getLocalDate(row?.completionDate, clinicTimezone)
    : "-";

  const formattedPatientName = useMemo(
    () => getTruncatedText(`${row?.firstName} ${row?.lastName}`),
    [row]
  );

  // sx specific are for small mobile screens
  return (
    <Card
      elevation={0}
      key={row.id}
      sx={{
        mb: "16px",
        py: "8px",
        borderRadius: "8px",
        backgroundColor: "#202338",
        color: "#FFF",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
        height: { xs: 89, xxs: 52, sm: 52 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        px={1}
        py={0}
        sx={{
          display: { xs: "grid", xxs: "flex", sm: "flex" },
          gridTemplateColumns: { xs: "5% 85% 10%", xxs: "none", sm: "none" },
          alignItems: "center",
        }}
      >
        <Grid item xxs={0} sm={0} md={0.5} />
        <Grid
          item
          xxs={11}
          sm={11}
          md={10.5}
          display={{ sx: "block", xxs: "flex", sm: "flex" }}
          flexDirection={{ xxs: "space-between", sm: "space-between" }}
        >
          <Grid item xs={12} xxs={4} sm={4}>
            <Typography
              sx={{
                display: { xs: "block", xxs: "none", sm: "none", md: "none" },
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              <b>{headCells[0].label}:</b> {formattedPatientName}
            </Typography>
            <Typography
              noWrap
              sx={{
                display: { xs: "none", xxs: "block", sm: "block", md: "block" },
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              {formattedPatientName}
            </Typography>
          </Grid>
          <Tooltip
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {examTooltipTranslation}
              </div>
            }
            placement="bottom-start"
          >
            <Grid item xs={12} xxs={5} sm={5}>
              <Typography
                sx={{
                  display: { xs: "block", xxs: "none", sm: "none", md: "none" },
                  fontSize: { xs: "12px", md: "14px" },
                }}
              >
                <b>{headCells[1].label}:</b>
                {examTranslation}
              </Typography>
              <Typography
                noWrap
                sx={{
                  display: {
                    xs: "none",
                    xxs: "block",
                    sm: "block",
                    md: "block",
                  },
                  fontSize: { xs: "12px", md: "14px" },
                }}
              >
                {examTranslation}
              </Typography>
            </Grid>
          </Tooltip>
          <Grid item xs={12} xxs={3}>
            <Typography
              sx={{
                display: { xs: "block", xxs: "none", sm: "none", md: "none" },
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              <b>{headCells[2].label}:</b> {doneLocalTime.slice(0, 10)}
            </Typography>
            <Typography
              noWrap
              sx={{
                display: { xs: "none", xxs: "block", sm: "block", md: "block" },
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              {doneLocalTime.slice(0, 10)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={headCells[3].gridSize} xxs={headCells[3].gridSize}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", xxs: "end" },
            }}
          >
            <Tooltip title={t("word_view_results")}>
              <span>
                <a
                  href={recentlyCompletedHref}
                  style={{ textDecoration: "none", color: "inherit" }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(recentlyCompletedHref);
                  }}
                >
                  <IconButton
                    sx={{
                      height: "40px",
                      color: "inherit",
                    }}
                  >
                    <ViewFileIcon />
                  </IconButton>
                </a>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default RecentlyCompletedExamCard;
